// ai_admin_v1 — SuperAdmin: AI profit dashboard + token-package & rate management
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';

const SA = axios.create({ baseURL: window.location.origin });
const authH = () => ({ headers: { Authorization: 'Bearer ' + localStorage.getItem('superadmin_token') } });
const nf = (n) => Number(n || 0).toLocaleString();
const baht = (n) => '฿' + Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });

const card = { background: '#1e293b', border: '1px solid #334155', borderRadius: 12, padding: 16 };
const inp = { background: '#0f172a', border: '1px solid #334155', borderRadius: 6, color: '#e2e8f0', padding: '6px 8px', fontSize: 13, width: '100%', boxSizing: 'border-box' };
const th = { padding: 8, textAlign: 'left', color: '#64748b', fontSize: 12, borderBottom: '1px solid #334155' };
const td = { padding: 8, color: '#e2e8f0', fontSize: 13, borderBottom: '1px solid #1e293b' };
const btn = (bg) => ({ background: bg, color: '#fff', border: 'none', borderRadius: 6, padding: '6px 12px', fontWeight: 700, cursor: 'pointer', fontSize: 13 });

function Stat({ label, value, color }) {
  return (
    <div style={{ ...card, flex: 1, minWidth: 150 }}>
      <div style={{ color: '#64748b', fontSize: 12 }}>{label}</div>
      <div style={{ color: color || '#e2e8f0', fontSize: 22, fontWeight: 800, marginTop: 4 }}>{value}</div>
    </div>
  );
}

export default function SuperAdminAI() {
  const [tab, setTab] = useState('report');
  const [report, setReport] = useState(null);
  const [forecast, setForecast] = useState(null); /* ai_forecast_v1 */
  const [costCap, setCostCap] = useState(null); /* ai_costcap_v1 */
  const [capInput, setCapInput] = useState('');
  const [tenants, setTenants] = useState([]); /* ai_trial_v1 */
  const [trialForm, setTrialForm] = useState({ tenant_id: '', tokens: 200000, days: 3 });
  const [trialBusy, setTrialBusy] = useState(false);
  const [pkgs, setPkgs] = useState([]);
  const [rates, setRates] = useState([]);
  /* slip_topup_v1 */
  const [spkgs, setSpkgs] = useState([]);
  const [sreport, setSreport] = useState(null);
  const [scost, setScost] = useState('');

  const loadReport = useCallback(async () => {
    try { setReport((await SA.get('/api/ai/admin/report', authH())).data); } catch (e) { toast.error('โหลดรายงานไม่สำเร็จ'); }
    try { setForecast((await SA.get('/api/ai/admin/forecast', authH())).data); } catch (e) { /* ai_forecast_v1 */ }
    try { const c = (await SA.get('/api/ai/admin/cost-cap', authH())).data; setCostCap(c); setCapInput(c.cap ? String(c.cap) : ''); } catch (e) { /* ai_costcap_v1 */ }
    try { const tn = (await SA.get('/api/superadmin/tenants', authH())).data; setTenants(Array.isArray(tn) ? tn : (tn.tenants || [])); } catch (e) { /* ai_trial_v1 */ }
  }, []);
  const grantTrial = async () => {
    if (!trialForm.tenant_id) return toast.error('เลือกร้าน');
    setTrialBusy(true);
    try {
      const r = await SA.post('/api/ai/admin/grant-trial', trialForm, authH());
      toast.success(`ให้ทดลอง ${Number(trialForm.tokens).toLocaleString()} โทเคน ${trialForm.days} วันแล้ว (หมดอายุ ${new Date(r.data.expires).toLocaleDateString('th-TH')})`);
    } catch (e) { toast.error(e.response?.data?.error || 'ไม่สำเร็จ'); }
    setTrialBusy(false);
  };
  const saveCap = async () => {
    try {
      await SA.put('/api/ai/admin/cost-cap', { cap: Number(capInput) || 0 }, authH());
      toast.success(Number(capInput) > 0 ? 'ตั้งเพดานแล้ว' : 'ปิดเพดานแล้ว');
      loadReport();
    } catch (e) { toast.error('บันทึกไม่สำเร็จ'); }
  };
  const loadPkgs = useCallback(async () => {
    try { setPkgs((await SA.get('/api/ai/admin/token-packages', authH())).data); } catch (e) { toast.error('โหลดแพ็กเกจไม่สำเร็จ'); }
  }, []);
  const loadRates = useCallback(async () => {
    try { setRates((await SA.get('/api/ai/admin/rates', authH())).data); } catch (e) { toast.error('โหลดเรตไม่สำเร็จ'); }
  }, []);

  const loadSpkgs = useCallback(async () => {
    try { setSpkgs((await SA.get('/api/slip-verify/admin/packages', authH())).data); } catch (e) { toast.error('โหลดแพ็กสลิปไม่สำเร็จ'); }
  }, []);
  const loadSreport = useCallback(async () => {
    try { const r = (await SA.get('/api/slip-verify/admin/sales-report', authH())).data; setSreport(r); setScost(r.cost_per_slip ? String(r.cost_per_slip) : ''); } catch (e) { /* silent */ }
  }, []);

  useEffect(() => {
    if (tab === 'report') loadReport();
    if (tab === 'packages') loadPkgs();
    if (tab === 'rates') loadRates();
    if (tab === 'slips') { loadSpkgs(); loadSreport(); }
  }, [tab, loadReport, loadPkgs, loadRates, loadSpkgs, loadSreport]);

  const setPkgField = (i, k, v) => setPkgs((a) => a.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  const savePkg = async (p) => { try { await SA.put('/api/ai/admin/token-packages/' + p.id, p, authH()); toast.success('บันทึกแพ็กเกจแล้ว'); loadPkgs(); } catch (e) { toast.error(e.response?.data?.error || 'ล้มเหลว'); } };
  const addPkg = async () => { try { await SA.post('/api/ai/admin/token-packages', { name: 'แพ็กเกจใหม่', tokens: 1000000, bonus_tokens: 0, price_thb: 99, is_active: true, sort_order: 99 }, authH()); loadPkgs(); } catch (e) { toast.error('ล้มเหลว'); } };
  const delPkg = async (id) => { if (!window.confirm('ลบแพ็กเกจนี้?')) return; try { await SA.delete('/api/ai/admin/token-packages/' + id, authH()); loadPkgs(); } catch (e) { toast.error('ล้มเหลว'); } };

  const setRateField = (i, k, v) => setRates((a) => a.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  const saveRate = async (r) => { try { await SA.put('/api/ai/admin/rates/' + r.id, r, authH()); toast.success('บันทึกเรตแล้ว'); loadRates(); } catch (e) { toast.error(e.response?.data?.error || 'ล้มเหลว'); } };

  /* slip_topup_v1 handlers */
  const setSpkgField = (i, k, v) => setSpkgs((a) => a.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  const saveSpkg = async (p) => { try { await SA.put('/api/slip-verify/admin/packages/' + p.id, p, authH()); toast.success('บันทึกแพ็กแล้ว'); loadSpkgs(); } catch (e) { toast.error(e.response?.data?.error || 'ล้มเหลว'); } };
  const addSpkg = async () => { try { await SA.post('/api/slip-verify/admin/packages', { name: 'แพ็กสลิปใหม่', slips: 1000, bonus_slips: 0, price_thb: 300, is_active: true, sort_order: 99 }, authH()); loadSpkgs(); } catch (e) { toast.error('ล้มเหลว'); } };
  const delSpkg = async (id) => { if (!window.confirm('ลบแพ็กนี้?')) return; try { await SA.delete('/api/slip-verify/admin/packages/' + id, authH()); loadSpkgs(); } catch (e) { toast.error('ล้มเหลว'); } };
  const saveScost = async () => { try { await SA.put('/api/slip-verify/admin/cost-per-slip', { cost_per_slip: Number(scost) || 0 }, authH()); toast.success('บันทึกต้นทุนแล้ว'); loadSreport(); } catch (e) { toast.error('ล้มเหลว'); } };

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {[['report', '📊 ภาพรวม/กำไร'], ['packages', '📦 แพ็กเกจโทเคน'], ['rates', '⚙️ เรตคิดเงิน'], ['slips', '🧾 ขายสลิป']].map(([k, t]) => (
          <button key={k} onClick={() => setTab(k)} style={{
            ...btn(tab === k ? '#0ea5e9' : '#334155'), padding: '8px 16px',
          }}>{t}</button>
        ))}
      </div>

      {tab === 'report' && report && (
        <>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
            <Stat label="รายได้ (ขายโทเคน)" value={baht(report.revenue)} color="#22c55e" />
            <Stat label="ต้นทุน provider (ใช้จริง)" value={baht(report.provider_cost)} color="#f59e0b" />
            <Stat label="กำไรขั้นต้น" value={baht(report.gross_margin)} color={report.gross_margin >= 0 ? '#22c55e' : '#ef4444'} />
            <Stat label="รออนุมัติ" value={nf(report.pending_orders) + ' ออเดอร์'} color="#0ea5e9" />
          </div>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 20 }}>
            <Stat label="โทเคนที่ขายได้" value={nf(report.tokens_sold)} />
            <Stat label="โทเคนที่ถูกใช้" value={nf(report.tokens_used)} />
            <Stat label="เรียก AI ทั้งหมด" value={nf(report.calls_all) + ' ครั้ง'} />
            <Stat label="ต้นทุน 30 วัน" value={baht(report.cost_30d)} />
          </div>

          {/* ai_trial_v1: grant expiring trial tokens */}
          <div style={{ ...card, marginBottom: 16 }}>
            <h3 style={{ margin: '0 0 4px', color: '#e2e8f0' }}>🎁 ให้โทเคนทดลอง (มีวันหมดอายุ)</h3>
            <p style={{ color: '#64748b', fontSize: 12, marginTop: 0 }}>
              โทเคนทดลองจะถูกใช้ก่อนเสมอ และ<b>หมดอายุอัตโนมัติ</b>ตามวันที่ตั้ง — ไม่ทบเข้าโทเคนที่ลูกค้าซื้อจริง
            </p>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <select style={{ ...inp, width: 200 }} value={trialForm.tenant_id}
                onChange={(e) => setTrialForm({ ...trialForm, tenant_id: e.target.value })}>
                <option value="">— เลือกร้าน —</option>
                {tenants.map((t) => <option key={t.id} value={t.id}>{t.name || t.email}</option>)}
              </select>
              <input type="number" min="1" style={{ ...inp, width: 140 }} value={trialForm.tokens}
                onChange={(e) => setTrialForm({ ...trialForm, tokens: e.target.value })} />
              <span style={{ fontSize: 13, color: '#94a3b8' }}>โทเคน</span>
              <input type="number" min="1" max="90" style={{ ...inp, width: 70 }} value={trialForm.days}
                onChange={(e) => setTrialForm({ ...trialForm, days: e.target.value })} />
              <span style={{ fontSize: 13, color: '#94a3b8' }}>วัน</span>
              <button style={btn(trialBusy ? '#334155' : '#22c55e')} disabled={trialBusy} onClick={grantTrial}>
                {trialBusy ? '...' : 'ให้ทดลอง'}
              </button>
            </div>
            <div style={{ fontSize: 11, color: '#64748b', marginTop: 6 }}>เช่น 200,000 โทเคน = ~100 ข้อความ ทดลอง 3 วัน</div>
          </div>

          {/* ai_costcap_v1: hard monthly cost ceiling */}
          {costCap && (
            <div style={{ ...card, marginBottom: 16, borderColor: costCap.capped ? '#dc2626' : '#334155' }}>
              <h3 style={{ margin: '0 0 4px', color: '#e2e8f0' }}>🛑 เพดานต้นทุน Google/เดือน (หยุด AI อัตโนมัติ)</h3>
              <p style={{ color: '#64748b', fontSize: 12, marginTop: 0 }}>
                เกินเพดานเมื่อไหร่ → หยุดเรียก Gemini ทันที (บอทตอบข้อความสำรอง + คีย์เวิร์ด/แคชยังทำงานฟรี) + แจ้ง Telegram · ใส่ 0 = ปิดเพดาน
              </p>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <span style={{ fontSize: 13, color: '#94a3b8' }}>เพดาน ฿</span>
                <input type="number" min="0" style={{ ...inp, width: 120 }} value={capInput}
                  onChange={(e) => setCapInput(e.target.value)} placeholder="เช่น 3000" />
                <span style={{ fontSize: 13, color: '#94a3b8' }}>/เดือน</span>
                <button style={btn('#0ea5e9')} onClick={saveCap}>บันทึก</button>
                <span style={{ fontSize: 13, color: '#94a3b8', marginLeft: 8 }}>
                  ใช้ไปเดือนนี้: <b style={{ color: costCap.capped ? '#ef4444' : '#22c55e' }}>{baht(costCap.spent_this_month)}</b>
                  {costCap.cap > 0 && ` / ${baht(costCap.cap)}`}
                </span>
              </div>
              {costCap.cap > 0 && (
                <div style={{ marginTop: 10, height: 8, background: '#0f172a', borderRadius: 4, overflow: 'hidden' }}>
                  <div style={{ height: 8, width: Math.min(100, (costCap.spent_this_month / costCap.cap) * 100) + '%',
                    background: costCap.capped ? '#dc2626' : costCap.spent_this_month / costCap.cap > 0.8 ? '#f59e0b' : '#22c55e' }} />
                </div>
              )}
              {costCap.capped && <div style={{ marginTop: 8, color: '#fca5a5', fontSize: 13 }}>⛔ ถึงเพดานแล้ว — AI หยุดเรียก Gemini (เพิ่มเพดานเพื่อเปิดต่อ)</div>}
            </div>
          )}

          {/* ai_forecast_v1: projected cost/profit from real inbound volume */}
          {forecast && (
            <div style={{ ...card, marginBottom: 16, borderColor: forecast.free_tier.exceeds ? '#b45309' : '#334155' }}>
              <h3 style={{ margin: '0 0 4px', color: '#e2e8f0' }}>📈 ประมาณการ (ถ้าเปิด AI ทุกร้าน)</h3>
              <p style={{ color: '#64748b', fontSize: 12, marginTop: 0 }}>
                คำนวณสดจากปริมาณข้อความจริง — เฉลี่ย {nf(forecast.inbound_avg_day)} ข้อความ/วัน (พีค {nf(forecast.inbound_peak_day)}),
                ~{Math.round(forecast.tokens_per_msg.input)}/{Math.round(forecast.tokens_per_msg.output)} tokens ต่อการตอบ,
                แคช/คีย์เวิร์ดช่วยกรอง {Math.round(forecast.cache_keyword_hit_rate * 100)}%
              </p>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <Stat label="ต้นทุน Google (วันปกติ)" value={baht(forecast.google_cost.typical_day) + '/วัน'} color="#f59e0b" />
                <Stat label="ต้นทุน Google (วันพีค)" value={baht(forecast.google_cost.peak_day) + '/วัน'} color="#f59e0b" />
                <Stat label="ต้นทุนต่อเดือน (ประมาณ)" value={baht(forecast.google_cost.month)} color="#f59e0b" />
                <Stat label="AI ตอบ/วัน (ประมาณ)" value={nf(forecast.ai_calls_per_day) + ' ครั้ง'} color="#0ea5e9" />
              </div>
              <div style={{
                marginTop: 12, padding: '10px 14px', borderRadius: 8, fontSize: 13,
                background: forecast.free_tier.exceeds ? '#78350f' : '#064e3b',
                color: forecast.free_tier.exceeds ? '#fed7aa' : '#a7f3d0',
              }}>
                {forecast.free_tier.exceeds ? (
                  <>⚠️ <b>จะทะลุ Free tier</b> (คาด {nf(forecast.free_tier.projected_rpd)} req/วัน &gt; ลิมิตฟรี ~{nf(forecast.free_tier.rpd_limit)}/วัน)
                    → ควร <b>ผูกบัตร (Set up billing)</b> ที่ Google AI Studio + ตั้ง budget แนะนำ <b>{baht(forecast.free_tier.suggested_daily_budget_thb)}/วัน</b></>
                ) : (
                  <>✅ ยังอยู่ในโควตา Free tier (คาด {nf(forecast.free_tier.projected_rpd)} req/วัน &lt; ~{nf(forecast.free_tier.rpd_limit)}/วัน) — ยังไม่ต้องผูกบัตร</>
                )}
              </div>
              <div style={{ marginTop: 8, fontSize: 11, color: '#64748b' }}>{forecast.pricing_note}</div>
            </div>
          )}

          <div style={{ ...card, marginBottom: 16 }}>
            <h3 style={{ margin: '0 0 10px', color: '#e2e8f0' }}>แยกตามโมเดล</h3>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr><th style={th}>Provider / Model</th><th style={th}>เรียก</th><th style={th}>โทเคนที่หัก</th><th style={th}>ต้นทุน</th></tr></thead>
              <tbody>
                {report.by_model.length === 0 && <tr><td style={td} colSpan={4}>ยังไม่มีข้อมูล</td></tr>}
                {report.by_model.map((m, i) => (
                  <tr key={i}><td style={td}>{m.provider} / {m.model}</td><td style={td}>{nf(m.calls)}</td><td style={td}>{nf(m.charged)}</td><td style={td}>{baht(m.cost)}</td></tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={card}>
            <h3 style={{ margin: '0 0 10px', color: '#e2e8f0' }}>แยกตามร้าน (Top 20)</h3>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr><th style={th}>ร้าน</th><th style={th}>เรียก</th><th style={th}>โทเคนที่หัก</th><th style={th}>ต้นทุน</th></tr></thead>
              <tbody>
                {report.by_tenant.length === 0 && <tr><td style={td} colSpan={4}>ยังไม่มีข้อมูล</td></tr>}
                {report.by_tenant.map((m, i) => (
                  <tr key={i}><td style={td}>{m.tenant || '(ไม่ระบุ)'}</td><td style={td}>{nf(m.calls)}</td><td style={td}>{nf(m.charged)}</td><td style={td}>{baht(m.cost)}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {tab === 'packages' && (
        <div style={card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
            <h3 style={{ margin: 0, color: '#e2e8f0' }}>แพ็กเกจโทเคน</h3>
            <button style={btn('#22c55e')} onClick={addPkg}>+ เพิ่มแพ็กเกจ</button>
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr><th style={th}>ชื่อ</th><th style={th}>โทเคน</th><th style={th}>โบนัส</th><th style={th}>ราคา ฿</th><th style={th}>ลำดับ</th><th style={th}>เปิด</th><th style={th}></th></tr></thead>
            <tbody>
              {pkgs.map((p, i) => (
                <tr key={p.id}>
                  <td style={td}><input style={inp} value={p.name || ''} onChange={(e) => setPkgField(i, 'name', e.target.value)} /></td>
                  <td style={td}><input style={inp} type="number" value={p.tokens} onChange={(e) => setPkgField(i, 'tokens', e.target.value)} /></td>
                  <td style={td}><input style={inp} type="number" value={p.bonus_tokens} onChange={(e) => setPkgField(i, 'bonus_tokens', e.target.value)} /></td>
                  <td style={td}><input style={inp} type="number" value={p.price_thb} onChange={(e) => setPkgField(i, 'price_thb', e.target.value)} /></td>
                  <td style={td}><input style={{ ...inp, width: 55 }} type="number" value={p.sort_order} onChange={(e) => setPkgField(i, 'sort_order', e.target.value)} /></td>
                  <td style={td}><input type="checkbox" checked={!!p.is_active} onChange={(e) => setPkgField(i, 'is_active', e.target.checked)} /></td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>
                    <button style={{ ...btn('#0ea5e9'), marginRight: 6 }} onClick={() => savePkg(p)}>บันทึก</button>
                    <button style={btn('#ef4444')} onClick={() => delPkg(p.id)}>ลบ</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'slips' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {sreport && (
            <div style={card}>
              <h3 style={{ margin: '0 0 12px', color: '#e2e8f0' }}>💰 ยอดขายสลิป / กำไรส่วนต่าง</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(120px,1fr))', gap: 12, marginBottom: 12 }}>
                <Stat label="รายได้รวม" value={baht(sreport.revenue)} color="#22c55e" />
                <Stat label="สลิปที่ขาย" value={Number(sreport.slips_sold).toLocaleString()} color="#0ea5e9" />
                <Stat label="ต้นทุน (slip2go)" value={baht(sreport.cost)} color="#f59e0b" />
                <Stat label="กำไร" value={baht(sreport.margin)} color={sreport.margin >= 0 ? '#22c55e' : '#ef4444'} />
                <Stat label="ออเดอร์" value={sreport.orders} color="#e2e8f0" />
                <Stat label="รออนุมัติ" value={sreport.pending} color="#f59e0b" />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, borderTop: '1px solid #334155', paddingTop: 12 }}>
                <span style={{ color: '#94a3b8', fontSize: 13 }}>ต้นทุน/สลิป ที่ซื้อจาก slip2go (฿):</span>
                <input style={{ ...inp, width: 100 }} type="number" step="0.01" value={scost} onChange={(e) => setScost(e.target.value)} placeholder="0.00" />
                <button style={btn('#0ea5e9')} onClick={saveScost}>บันทึก</button>
                <span style={{ color: '#64748b', fontSize: 12 }}>ใช้คำนวณกำไร</span>
              </div>
            </div>
          )}

          <div style={card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
              <h3 style={{ margin: 0, color: '#e2e8f0' }}>แพ็กสลิปที่ขายให้ tenant</h3>
              <button style={btn('#22c55e')} onClick={addSpkg}>+ เพิ่มแพ็ก</button>
            </div>
            <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr><th style={th}>ชื่อ</th><th style={th}>สลิป</th><th style={th}>โบนัส</th><th style={th}>ราคา ฿</th><th style={th}>ลำดับ</th><th style={th}>เปิด</th><th style={th}></th></tr></thead>
              <tbody>
                {spkgs.map((p, i) => (
                  <tr key={p.id}>
                    <td style={td}><input style={inp} value={p.name || ''} onChange={(e) => setSpkgField(i, 'name', e.target.value)} /></td>
                    <td style={td}><input style={{ ...inp, width: 90 }} type="number" value={p.slips} onChange={(e) => setSpkgField(i, 'slips', e.target.value)} /></td>
                    <td style={td}><input style={{ ...inp, width: 80 }} type="number" value={p.bonus_slips} onChange={(e) => setSpkgField(i, 'bonus_slips', e.target.value)} /></td>
                    <td style={td}><input style={{ ...inp, width: 90 }} type="number" value={p.price_thb} onChange={(e) => setSpkgField(i, 'price_thb', e.target.value)} /></td>
                    <td style={td}><input style={{ ...inp, width: 55 }} type="number" value={p.sort_order} onChange={(e) => setSpkgField(i, 'sort_order', e.target.value)} /></td>
                    <td style={td}><input type="checkbox" checked={!!p.is_active} onChange={(e) => setSpkgField(i, 'is_active', e.target.checked)} /></td>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>
                      <button style={{ ...btn('#0ea5e9'), marginRight: 6 }} onClick={() => saveSpkg(p)}>บันทึก</button>
                      <button style={btn('#ef4444')} onClick={() => delSpkg(p.id)}>ลบ</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </div>

          {sreport && sreport.by_tenant && sreport.by_tenant.length > 0 && (
            <div style={card}>
              <h3 style={{ margin: '0 0 10px', color: '#e2e8f0' }}>ยอดซื้อแยกตามร้าน</h3>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr><th style={th}>ร้าน</th><th style={th}>ออเดอร์</th><th style={th}>สลิป</th><th style={th}>รายได้</th></tr></thead>
                <tbody>
                  {sreport.by_tenant.map((t, i) => (
                    <tr key={i}><td style={td}>{t.name}</td><td style={td}>{t.orders}</td><td style={td}>{Number(t.slips).toLocaleString()}</td><td style={td}>{baht(t.revenue)}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {tab === 'rates' && (
        <div style={card}>
          <h3 style={{ margin: '0 0 4px', color: '#e2e8f0' }}>เรตคิดเงิน (โทเคนที่หักต่อ 1,000 LLM tokens)</h3>
          <p style={{ color: '#64748b', fontSize: 12, marginTop: 0 }}>charge = โทเคนที่หักจากลูกค้า · cost = ต้นทุนจริงเรา (บาท/1M) ใช้คำนวณกำไร</p>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr><th style={th}>โมเดล</th><th style={th}>หัก/1k in</th><th style={th}>หัก/1k out</th><th style={th}>ต้นทุน ฿/1M in</th><th style={th}>ต้นทุน ฿/1M out</th><th style={th}>เปิด</th><th style={th}></th></tr></thead>
            <tbody>
              {rates.map((r, i) => (
                <tr key={r.id}>
                  <td style={td}>{r.provider}<br /><span style={{ color: '#64748b', fontSize: 11 }}>{r.model}</span></td>
                  <td style={td}><input style={{ ...inp, width: 80 }} type="number" value={r.charge_per_1k_input} onChange={(e) => setRateField(i, 'charge_per_1k_input', e.target.value)} /></td>
                  <td style={td}><input style={{ ...inp, width: 80 }} type="number" value={r.charge_per_1k_output} onChange={(e) => setRateField(i, 'charge_per_1k_output', e.target.value)} /></td>
                  <td style={td}><input style={{ ...inp, width: 80 }} type="number" step="0.01" value={r.cost_thb_per_1m_input} onChange={(e) => setRateField(i, 'cost_thb_per_1m_input', e.target.value)} /></td>
                  <td style={td}><input style={{ ...inp, width: 80 }} type="number" step="0.01" value={r.cost_thb_per_1m_output} onChange={(e) => setRateField(i, 'cost_thb_per_1m_output', e.target.value)} /></td>
                  <td style={td}><input type="checkbox" checked={!!r.is_active} onChange={(e) => setRateField(i, 'is_active', e.target.checked)} /></td>
                  <td style={td}><button style={btn('#0ea5e9')} onClick={() => saveRate(r)}>บันทึก</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
